/* eslint-disable no-param-reassign */
// Reference writes update the grid's live cell objects and the cached store securities in place, on purpose.
import { partition } from 'lodash';
import type { SecurityCitationCreateBody, SecurityCitationCreateResult, SecurityMappingCreateResult } from 'services/captable';
import type { CitationReferenceData, MappingReferenceData } from 'services/reference-generator/types';
import convertCamelCaseToSnakeCase from 'utilities/convertCamelCaseToSnakeCase/convertCamelCaseToSnakeCase';
import { extractResponseErrorMessage } from 'utilities/extractResponseErrorMessage';
import { withoutIds } from './clearEvidence';
import { manualCitationRequest } from './manualCitationAdd';
import { isEditableMappingReference, type MappingFlagged } from './mappingReferenceStatus';
import { citationReviewStatus } from './reviewAggregator';
import { syncReferenceGreen } from './securityAiPatches';
import { applyCitationReviewToRefs } from './settleCitationReview';

/** The cell fields a reference write touches. */
type ReferenceCell = {
  citationReferences?: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  retiredMappingIds?: number[];
  className?: string | null;
  dispatchEvent?: () => void;
};

/** The cached store security fields a reference write touches. */
export type ReferenceSecurity = {
  id?: number;
  name?: string;
  citation_references?: CitationReferenceData[];
  mapping_references?: MappingReferenceData[];
};

/** True for a security the server already stores; its references save through the create calls, not Save. */
export const isSavedSecurityId = (id: unknown): boolean => Number(id) > 0;

/**
 * The citations once the server's row is in: it replaces its id twin or joins the end, and the rows
 * a Replace dismissed read as dismissed.
 */
export const placeCreatedCitation = (
  refs: CitationReferenceData[] | undefined,
  { citation, dismissed_ids: dismissedIds }: SecurityCitationCreateResult
): CitationReferenceData[] => {
  const list = refs ?? [];
  const placed = list.some(ref => ref.id === citation.id)
    ? list.map(ref => (ref.id === citation.id ? citation : ref))
    : [...list, citation];
  return dismissedIds.reduce((next, id) => applyCitationReviewToRefs(next, id, 'dismiss') ?? next, placed);
};

/**
 * The Excel references once the server's pick is in: the rows it retired go, and it replaces its id twin
 * or leads the list as the cell's reference.
 */
export const placeCreatedMapping = (
  refs: MappingReferenceData[] | undefined,
  { mapping, retired_ids: retiredIds }: SecurityMappingCreateResult
): MappingReferenceData[] => {
  const kept: MappingReferenceData[] = withoutIds(refs, new Set(retiredIds)) ?? [];
  return kept.some(ref => ref.id === mapping.id)
    ? kept.map(ref => (ref.id === mapping.id ? mapping : ref))
    : [mapping, ...kept];
};

/** Writes a saved citation onto the cell and, in place, onto its store security, so a grid rebuild keeps it. */
export const writeCreatedCitation = (
  cell: ReferenceCell | undefined,
  security: ReferenceSecurity | undefined,
  result: SecurityCitationCreateResult
): void => {
  if (cell) {
    cell.citationReferences = placeCreatedCitation(cell.citationReferences, result);
    cell.dispatchEvent?.();
  }
  if (security) {
    security.citation_references = placeCreatedCitation(security.citation_references, result);
  }
};

/**
 * Writes a saved Excel pick onto the cell (green, and off the next Save's retire list) and, in place,
 * onto its store security, so a grid rebuild keeps it.
 */
export const writeCreatedMapping = (
  cell: ReferenceCell | undefined,
  security: ReferenceSecurity | undefined,
  result: SecurityMappingCreateResult
): void => {
  if (cell) {
    cell.mappingReferences = placeCreatedMapping(cell.mappingReferences, result);
    cell.retiredMappingIds = cell.retiredMappingIds?.filter(id => id !== result.mapping.id);
    syncReferenceGreen(cell);
    cell.dispatchEvent?.();
  }
  if (security) {
    security.mapping_references = placeCreatedMapping(security.mapping_references, result);
  }
};

/**
 * True when an edit leaves the value as entered: numbers compare by value, anything else (an empty cell included)
 * by its trimmed text, so clearing a zero counts as a change.
 */
export const isSameEnteredValue = (before: unknown, after: unknown): boolean => {
  const [was, now] = [before, after].map(value => String(value ?? '').trim());
  const [wasNumber, nowNumber] = [was, now].map(text => (text === '' ? NaN : Number(text)));
  return Number.isFinite(wasNumber) && Number.isFinite(nowNumber) ? wasNumber === nowNumber : was === now;
};

/**
 * Takes the cell's editable Excel references off and remembers the saved ones, so the next Save retires them;
 * AI proposals stay for review. True when anything was taken off.
 */
export const stripEditableMappings = (cell: ReferenceCell): boolean => {
  const [removed, kept] = partition(cell.mappingReferences ?? [], isEditableMappingReference);
  if (removed.length === 0) {
    return false;
  }
  const savedIds = removed.flatMap(ref => (ref.id != null ? [ref.id] : []));
  cell.mappingReferences = kept;
  cell.retiredMappingIds = [...new Set([...(cell.retiredMappingIds ?? []), ...savedIds])];
  return true;
};

/** The security fields Save reads for references; every other field passes through unchanged. */
type SaveSecurity = {
  id?: number | string | null;
  security_ref?: string | null;
  citation_references?: unknown;
  // The generated security type calls these strings; at runtime they are the cells' Excel reference rows.
  mapping_references?: unknown;
  retire_mapping_ids?: number[] | null;
};

/** Keep failed citation selections on their committed security without carrying over a clone's source ids. */
export const preserveCitationDrafts = <T extends SaveSecurity>(
  saved: T[],
  submitted: T[],
  includeNew = false
): T[] => {
  const submittedByRef = new Map(submitted.filter(security => security.security_ref).map(security => [security.security_ref, security]));
  return saved.map(security => {
    const original = submittedByRef.get(security.security_ref);
    const citations = (security.citation_references ?? []) as CitationReferenceData[];
    const drafts = ((original?.citation_references ?? []) as CitationReferenceData[]).filter(ref =>
      (ref.pending_save || (includeNew && !isSavedSecurityId(original?.id))) &&
      citationReviewStatus(ref) === 'accepted' &&
      !citations.some(savedRef => savedRef.document_reference === ref.document_reference && savedRef.field_name === ref.field_name)
    );
    return drafts.length ? {
      ...security,
      citation_references: [...citations, ...drafts.map(({ id, ...ref }) => ({ ...ref, pending_save: true }))],
    } : security;
  });
};

/** Remove only the selected security's pending draft, leaving server citations untouched. */
export const removePendingCitationDraft = <T extends SaveSecurity>(
  securities: T[], securityRef: string | null | undefined, draft: CitationReferenceData
): T[] =>
    securities.map(security => {
      if (!securityRef || security.security_ref !== securityRef) {
        return security;
      }
      const refs = (security.citation_references ?? []) as CitationReferenceData[];
      const kept = refs.filter(ref => !(ref.pending_save &&
      ref.document_reference === draft.document_reference && ref.field_name === draft.field_name));
      return kept.length === refs.length ? security : { ...security, citation_references: kept };
    });

/** Attempt every pending citation and retain each failed selection while settling successful retries. */
export const savePendingCitationDrafts = async (
  securities: ReferenceSecurity[],
  createCitation: (securityId: number, body: SecurityCitationCreateBody) => Promise<SecurityCitationCreateResult>
): Promise<void> => {
  const failures: string[] = [];
  await securities.flatMap(security => (security.citation_references ?? [])
    .filter(ref => ref.pending_save)
    .map(draft => ({ security, draft })))
    .reduce(async (previous, { security, draft }) => {
      await previous;
      try {
        const result = await createCitation(Number(security.id), manualCitationRequest([draft], {
          documentReferenceId: draft.document_reference,
          fieldName: draft.field_name,
        }, 'append'));
        security.citation_references = placeCreatedCitation(
          security.citation_references?.filter(ref => ref !== draft), result
        );
      } catch (error) {
        const reason = extractResponseErrorMessage(error) ??
          (error instanceof Error ? error.message : 'Request failed');
        failures.push(`${security.name || 'Unnamed security'} - ${draft.field_name}: ${reason.slice(0, 180)}`);
      }
    }, Promise.resolve());
  if (failures.length) {
    throw new Error(failures.join('; '));
  }
};

/**
 * What a security's Save sends about references: a new security (a cloned column included) carries only its
 * accepted citations and editable Excel references, so carried AI proposals stay in review; a saved one sends
 * only the Excel references a manual edit removed.
 */
export const withSaveReferences = <T extends SaveSecurity>(security: T) => {
  const {
    citation_references: citations,
    mapping_references: mappings,
    retire_mapping_ids: retireIds,
    ...rest
  } = security;
  if (!isSavedSecurityId(security.id)) {
    return {
      ...rest,
      citation_references: convertCamelCaseToSnakeCase(
        (citations as CitationReferenceData[] | undefined)
          ?.filter(ref => citationReviewStatus(ref) === 'accepted')
          .map(({ pending_save, ...ref }) => ref)
      ),
      mapping_references: (mappings as MappingFlagged[] | undefined)?.filter(isEditableMappingReference),
    };
  }
  return retireIds?.length ? { ...rest, retire_mapping_ids: retireIds } : rest;
};
